/**
 * Normalizes a Firestore product document into a consistent format for the UI.
 * Handles missing fields, legacy field names, and image fallbacks.
 *
 * @param {Object} doc - The Firestore document snapshot (or an object with id and data).
 * @returns {Object} Normalized product object.
 */
export const normalizeProduct = (doc) => {
    // Handle both DocumentSnapshot and plain object with { id, ...data }
    const data = doc.data ? doc.data() : (doc || {});
    const id = doc.id || data.id;

    // Normalization logic
    return {
        id: id,
        name: data.title || data.name || "Untitled Product",
        // Image priority: imageUrl -> image -> images[0] -> empty string
        image: data.imageUrl || data.image || (Array.isArray(data.images) && data.images[0]) || "",
        description: data.description || data.desc || data.productDescription || "",
        category: (data.category || "").trim(),
        brand: data.brand || "",
        price: Number(data.price) || 0,
        stock: Number(data.stock) || 0,
        isActive: data.isActive !== false, // Default true if undefined
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        // Keep original data just in case
        _original: data,
    };
};

/**
 * Robust image error handler.
 * usage: <img onError={handleImageError} ... />
 */
export const handleImageError = (e) => {
    e.target.src = "https://placehold.co/400x400/e2e8f0/1e293b?text=No+Image";
    e.target.onerror = null; // Prevent infinite loops if placeholder fails
};

/**
 * Resolves the best available image source for a product.
 * @param {Object} product - Product object
 * @returns {string} - Image URL
 */
export const getProductImageSrc = (product) => {
    if (!product) return "https://placehold.co/400x400/e2e8f0/1e293b?text=No+Image";

    const src = product.image ||
        product.imageUrl ||
        (Array.isArray(product.images) && product.images[0]) ||
        product.thumbnail ||
        "";

    if (!src) return "https://placehold.co/400x400/e2e8f0/1e293b?text=No+Image";

    // Build absolute URL if relative (assuming backend files might be mostly absolute or simple paths)
    // If you had a specific base URL for uploads, you'd prepend it here.
    // Example: if (src.startsWith('/uploads')) return `https://api.fecosfood.com${src}`;

    return src;
};
